import { Button, Dialog, TextField, Typography } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";
import cuideoTheme from "../../../../../../containers/themes/defaultTheme";

const StyledDialog = styled(Dialog)``;

const StyledTitle = styled(Typography)`
  color: ${cuideoTheme.palette.primary.main};
  font-size: 1.5rem;
  font-weight: 700;
  margin: 1rem;
`;

const StyledLabel = styled(Typography)`
  color: ${cuideoTheme.palette.primary.main};
  font-size: 1rem;
  font-weight: 400;
  text-align: center;
  margin: 1rem;
`;

const StyledReasonWrapper = styled.div`
  margin: 1rem 1rem;
`;

const StyledReasonLabel = styled.label`
  display: block;
  font-family: "Source Sans Pro", Helvetica, Arial, sans-serif;
  font-size: 0.8rem;
  line-height: 1.5;
  padding: 0.2rem 0;
  color: ${cuideoTheme.palette.primary.light};
`;

/**
 * The theme pins every MuiInputBase root to 50px, so a multiline field needs
 * `height: auto` or the textarea overflows its own outline. Same fix, and the
 * same padding values, as FormikTextField at size "small".
 */
const StyledReasonField = styled(TextField)`
  .MuiOutlinedInput-root {
    height: auto;
    font-size: 1rem;
    background-color: ${cuideoTheme.palette.common.white};
    border-radius: ${cuideoTheme.shape.borderRadius}px;
    color: ${cuideoTheme.palette.primary.main};
  }
  .MuiOutlinedInput-multiline {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  }
  .MuiOutlinedInput-inputMultiline {
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
  }
`;

const StyledDialogActions = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 1rem 0.5rem 0.5rem;
`;

const StyledCancelButton = styled(Button)`
  margin: 0.5rem;
  flex-grow: 1;
`;

const StyledConfirmButton = styled(Button)`
  margin: 0.5rem;
  flex-grow: 1;
`;

const REASON_MIN_ROWS = 4;
const REASON_MAX_ROWS = 8;

// Material-UI v4 does not generate one, and without it the label is not bound
// to the textarea.
const REASON_FIELD_ID = "discard-valuations-reason";

interface IDiscardValuationsDialogProps {
  open: boolean;
  candidateNames: string[];
  /**
   * ECO-1293: only real valuations can store the reason. A selection made up of
   * suggestions and recommendations alone has nowhere to write it, so the field
   * is not asked for.
   */
  reasonRequired: boolean;
  /** Extra notice shown under the question, e.g. for hired candidates. */
  warning?: React.ReactNode;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

const DiscardValuationsDialog = (props: IDiscardValuationsDialogProps) => {
  const { open, candidateNames, reasonRequired, warning, onConfirm, onCancel } =
    props;

  const { formatMessage } = useIntl();
  const [reason, setReason] = useState("");

  // A reason belongs to the discard it was written for, never to the next one.
  useEffect(() => {
    if (open) {
      setReason("");
    }
  }, [open]);

  const trimmedReason = reason.trim();
  const isConfirmDisabled = reasonRequired && trimmedReason === "";

  const handleConfirmClick = () => {
    onConfirm(trimmedReason);
  };

  const handleCancelClick = () => {
    onCancel();
  };

  return (
    <StyledDialog
      open={open}
      className="MuiCuideo-DiscardValuationsDialog"
      fullWidth
      maxWidth="sm"
    >
      <StyledTitle>
        <FormattedMessage
          id="DiscardValuationsDialog.Descartar valoraciones"
          defaultMessage="Descartar valoraciones"
        />
      </StyledTitle>
      <StyledLabel>
        <FormattedMessage
          id="DiscardValuationsDialog.¿Seguro que quieres eliminar del contrato a {candidates}?"
          defaultMessage="¿Seguro que quieres eliminar del contrato a {candidates}?"
          values={{
            candidates: <b>{candidateNames.join(", ")}</b>,
          }}
        />
      </StyledLabel>
      {warning && <StyledLabel>{warning}</StyledLabel>}
      {reasonRequired && (
        <StyledReasonWrapper>
          <StyledReasonLabel htmlFor={REASON_FIELD_ID}>
            {formatMessage({
              id: "DiscardValuationsDialog.Motivo del descarte",
              defaultMessage: "Motivo del descarte",
            })}
          </StyledReasonLabel>
          <StyledReasonField
            id={REASON_FIELD_ID}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
            variant="outlined"
            multiline
            minRows={REASON_MIN_ROWS}
            maxRows={REASON_MAX_ROWS}
            required
            autoFocus
            fullWidth
          />
        </StyledReasonWrapper>
      )}
      <StyledDialogActions>
        <StyledCancelButton
          color="primary"
          variant="text"
          onClick={handleCancelClick}
          fullWidth
        >
          <FormattedMessage
            id="DiscardValuationsDialog.Cancelar"
            defaultMessage="Cancelar"
          />
        </StyledCancelButton>
        <StyledConfirmButton
          color="primary"
          variant="contained"
          onClick={handleConfirmClick}
          disabled={isConfirmDisabled}
          fullWidth
        >
          <FormattedMessage
            id="DiscardValuationsDialog.Confirmar"
            defaultMessage="Confirmar"
          />
        </StyledConfirmButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default DiscardValuationsDialog;
